import { format, isValid, parseISO } from 'date-fns';

import {
    composeTopicContextData,
    createClubContext, createCompetitionContext,
    createContentMetadataContext,
    createMatchContext, createPlayerContext,
    createTopicContext,
    getDynamicArticleData, getDynamicClubData, getDynamicPlayerData
} from "./snowplowTracking";

export function getSnowplowVariables() {
    const { tracking = {} } = window?.forgeVariables || {};

    return {
        appId: tracking?.appId,
        collectorUrl: tracking?.collectorUrl,
        channelName: tracking?.channelName,
        category: tracking?.category,
        nameSpace: tracking?.nameSpace,
        pageType: tracking?.pageType || "homepage",
        entityContexts: getEntityContextList(tracking?.entityContexts),
    }
}

function getEntityContextList(entityContexts) {
    return entityContexts?.split(',').filter(context => context !== "");
}


export function createPageContexts({
    mlsMatch,
    mlsTeam,
    mlsHomeTeam,
    mlsAwayTeam,
    mlsCompetition,
    mlsPlayer,
}) {
    const { entityContexts } = getSnowplowVariables();
    const contexts = [];

    entityContexts.forEach(entityContext => {
        if (entityContext === "match") {
            contexts.push(createMatchContext(mlsMatch));
        } else if (entityContext === "team" || entityContext === "club" || entityContext === "clubs") {
            if (mlsTeam) {
                contexts.push(createClubContext(mlsTeam));
            }
            if (mlsHomeTeam) {
                contexts.push(createClubContext(mlsHomeTeam));
            }
            if (mlsAwayTeam) {
                contexts.push(createClubContext(mlsAwayTeam));
            }
        }
        else if (entityContext === "competition") {
            contexts.push(createCompetitionContext(mlsCompetition));
        } else if (entityContext === "player") {
            contexts.push(createPlayerContext(mlsPlayer));
        }
    });

    return contexts;
}

/**
 * Returns the name of the schema.
 * @returns {string} - The name of the schema. player, club, article
 */
export function getSchemaName() {
    const { schema } = window?.forgeVariables?.tracking || {};

    return schema;
}

const schemaDataMapFunctions = {
    article: getDynamicArticleData,
    club: getDynamicClubData,
    player: getDynamicPlayerData,
}


/**
 * Returns the data for the dynamic schema.
 * @param {string} schemaName - The name of the schema. player, club, article
 * @returns {Object} - The data for the dynamic schema.
 */
export function getDynamicSchemaData(schemaName) {
    const schemaDataMapFunction = schemaDataMapFunctions[schemaName];

    if (!schemaDataMapFunction) {
        return undefined;
    }

    return schemaDataMapFunction();
}


/**
 * Returns a context object for the dynamic schema.
 * @returns {Object} - The context object for the dynamic schema.
 */
export function getDynamicSchemaContext(schemaName) {
    if (schemaName) {
        const schemaData = getDynamicSchemaData(schemaName);
        const createSchemaContextFunctions = {
            player: createPlayerContext,
            club: createClubContext,
            article: createContentMetadataContext,
            videoPlaylist: (video) => video
        }
        const schemaContext = createSchemaContextFunctions[schemaName](schemaData);

        return schemaContext;
    }

    return null;
}


const categories = ["C0002", "C0003", "C0004"];

export function hasUserAcceptedAllConsent() {
    const activeGroups = getActiveConsentGroups();

    return categories.every(category => activeGroups.includes(category));
}

export function hasUserAcceptedMeasurementConsent() {
    const activeGroups = getActiveConsentGroups();
    return activeGroups.includes("C0002");
}

export function hasUserGroup(group) {
    const activeGroups = getActiveConsentGroups();
    return activeGroups.includes(group);
}

function getActiveConsentGroups() {
    const activeGroups = window?.OptanonActiveGroups || "";
    const activeGroupsList = activeGroups?.split(',').filter(group => group !== "");

    return activeGroupsList;
}

export function getAvailableTopics(schemaName) {
    if (!schemaName) {
        return []
    }

    const { topics = [] } = window?.forgeVariables?.tracking?.[schemaName] || {};

    return topics?.map(topic => createTopicContext(
      composeTopicContextData({ id: topic?._entityId || topic?.slug, name: topic?.title, slug: topic?.slug, type: extractCustomEntityValue(topic?.externalSourceName ?? topic?.type) })
    ));
}

/**
 * Creates topic contexts from an array of tags, skipping the first N tags
 * @param {Array} tags - Array of tag objects
 * @param {number} skipCount - Number of tags to skip from the beginning (default: 2)
 * @returns {Array} Array of topic context objects
 */
export function createTopicContextsFromTags(tags = [], skipCount = 2) {
    if (!tags || tags?.length <= skipCount) {
        return [];
    }

    const additionalTags = tags.slice(skipCount);
    return additionalTags
        .map(tag => {
            const topicData = composeTopicContextData({
                id: tag?.slug || tag?._entityId,
                name: tag?.title,
                slug: tag?.slug,
                type: tag?.type
            });
            return createTopicContext(topicData);
        })
        .filter(context => context !== null);
}

/**
 * Validates if a value is a valid number and returns it, otherwise returns null
 * @param {*} value - The value to validate
 * @returns {number|null} - The validated number or null
 */
export function validateNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const num = Number(value);
    return isNaN(num) ? null : num;
}

/**
 * Executes a callback when Snowplow is initialized, with retry logic
 * @param {Function} callback - Function to execute when Snowplow is ready
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 10)
 * @param {number} options.retryInterval - Interval between retries in ms (default: 500)
 * @returns {Function} - Cleanup function to cancel pending retries
 */
export function runWhenSnowplowReady(callback, options = {}) {
    const { maxRetries = 10, retryInterval = 500 } = options;
    let retryCount = 0;
    let intervalId = null;
    let hasExecuted = false;

    const isSnowplowInitialized = () => {
        return window?.snowplow && typeof window.snowplow === 'function';
    };

    const tryExecute = () => {
        if (hasExecuted) {
            return true;
        }

        if (isSnowplowInitialized()) {
            hasExecuted = true;
            callback();
            return true;
        }

        retryCount += 1;

        if (retryCount >= maxRetries) {
            hasExecuted = true;
            return true;
        }

        return false;
    };

    const success = tryExecute();

    if (!success) {
        intervalId = setInterval(() => {
            const done = tryExecute();
            if (done && intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        }, retryInterval);
    }

    return () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
        hasExecuted = true;
    };
}

/**
 * Formats a date string to YYYY-MM-DD format
 * @param {string} dateString - The date string to format
 * @returns {string|null} - Formatted date string or null if invalid
 */
export function validateDate(dateString) {
    if (!dateString) {
        return null;
    }

    try {
        const date = parseISO(dateString);
        return isValid(date) ? format(date, 'yyyy-MM-dd') : null;
    } catch (error) {
        return null;
    }
}

/**
 * Validates and formats a date-time string to ISO 8601 format
 * @param {string} dateTimeString - The date-time string to validate
 * @returns {string|null} - ISO 8601 formatted date-time string or null if invalid
 */
export function validateDateTime(dateTimeString) {
    if (!dateTimeString) {
        return null;
    }

    try {
        const date = parseISO(dateTimeString);
        if (!isValid(date)) {
            return null;
        }

        // Return the original string if it's already in valid ISO format
        // Otherwise, format it to ISO 8601
        const isoString = date.toISOString();
        return isoString;
    } catch (error) {
        return null;
    }
}


export function getVideoContextsFromTags(tags = []) {
    const contexts = tags.map(tag => {
        return createVideoTagContext(tag);
    });

    return contexts.filter(context => context !== null);
}

// externalSourceName
const videoTagSchemaMap = {
    "customentity.topic": "topic",
    "customentity.match": "match",
    "customentity.competition": "competition",
    "customentity.club": "club",
    "customentity.player": "player",
}

export function getSchemaNameFromVideoTag(videoTag) {
    const tagType = videoTag?.externalSourceName || videoTag?.externalSourceReference?.sourceName;
    const schema = videoTagSchemaMap[tagType];

    if (!schema) {
        return null;
    }

    return schema;
}


const DEFAULT_TAG_VALUE = "Not Defined";
const getDefaultKickoffTime = () => new Date().toISOString();

export function createVideoTagContext(videoTag) {
    const schemaName = getSchemaNameFromVideoTag(videoTag);
    if (!schemaName) {
        return null;
    }

    switch (schemaName) {
        case "topic":
            return createTopicContext({
                topic_id: videoTag?._entityId || videoTag?.slug,
                topic_name: videoTag?.title,
                topic_slug: videoTag?.slug,
                topic_type: videoTag?.type,
            });
        case "match":
            return createMatchContext({
                // required values
                match_id: videoTag?.extraData?.sportecId || DEFAULT_TAG_VALUE,
                home_team: videoTag?.extraData?.homeClubName || DEFAULT_TAG_VALUE,
                away_team: videoTag?.extraData?.awayClubName || DEFAULT_TAG_VALUE,
                kickoff_time: videoTag?.extraData?.kickoffTime || getDefaultKickoffTime(),
            });
        case "competition":
            return createCompetitionContext({
                // required values
                competition_id: videoTag?.extraData?.sportecId || DEFAULT_TAG_VALUE,
                competition_name: videoTag?.extraData?.name || DEFAULT_TAG_VALUE,
                season: videoTag?.extraData?.seasonSportecId || DEFAULT_TAG_VALUE,
            });
        case "club":
            return createClubContext({
                // required values
                club_id: videoTag?.extraData?.sportecId || DEFAULT_TAG_VALUE,
                club_name: videoTag?.extraData?.name || DEFAULT_TAG_VALUE,
            });
        case "player":
            return createPlayerContext({
                // required values
                player_id: videoTag?.extraData?.sportecId || DEFAULT_TAG_VALUE,
                player_name: `${videoTag?.extraData?.firstName} ${videoTag?.extraData?.lastName}` || DEFAULT_TAG_VALUE,
                // optional values
                player_team: videoTag?.extraData?.clubSportecId,
            });
    }
}
/**
 * Extracts value of customEntity if its club player or match returns tag with club player match otherwise tag
 * @param {any} input
 * @returns
 */
export function extractCustomEntityValue(input) {
  const knownEntities = ['club', 'player', 'match'];

  if (input.startsWith('customentity.')) {
    const type = input.split('.')[1];
    return knownEntities.includes(type) ? type : 'tag';
  }

  return input;
}
